import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { 
  Auth,
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  User
} from 'firebase/auth';
import { api } from '../services/api';
import { UserProfile } from '../services/api';
import axios from 'axios';
import { getCurrentTenantConfig, getTenantId } from '../services/firebase-config';

// Get tenant-aware Firebase configuration
const tenantConfig = getCurrentTenantConfig();
const auth = tenantConfig.auth;

interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  databaseUserId: string | null;
  login: (email: string, password: string) => Promise<User>;
  loginWithGoogle: (profile?: { connection?: string; zip_code?: string; ward?: string }) => Promise<User>;
  signup: (email: string, password: string, profile: { displayName: string; connection: string; zip_code?: string; ward?: string }) => Promise<User>;
  logout: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
  updateUserProfile: (profile: { displayName: string; connection: string; zip_code?: string; ward?: string }) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [databaseUserId, setDatabaseUserId] = useState<string | null>(null);
  // Add a ref to track if we're in the signup process
  const skipProfileFetch = useRef(false);

  // Fetch user profile data from Postgres via API
  const fetchUserProfile = async (user: User) => {
    try {
      // Get user profile from API
      const response = await api.getUserProfileByUid(user.uid);
      
      if (response) {
        setUserProfile(response);
        
        // Set the database user_id if available in the profile
        if (response.database_uuid) {
          setDatabaseUserId(response.database_uuid);
          console.log('Set database user_id from profile:', response.database_uuid);
        } else {
          // Fallback to get it directly if not in the profile
          // This really should never happen, but just in case
          try {
            const dbUserId = await api.getUserDatabaseUUID(user.uid);
            if (dbUserId) {
              setDatabaseUserId(dbUserId);
              console.log('Set database user_id from API call:', dbUserId);
            } else {
              // If all else fails, use the firebase UID
              console.warn('No database user_id found, falling back to Firebase UID');
              setDatabaseUserId(user.uid);
            }
          } catch (error) {
            console.error('Error fetching database user_id:', error);
            setDatabaseUserId(user.uid);
          }
        }
      } else {
        // If no profile exists, use displayName from auth
        setUserProfile({
          uid: user.uid,
          displayName: user.displayName || user.email?.split('@')[0] || 'User',
          connection: '',
          email: user.email || ''
        });
        
        // Attempt to get database user_id
        try {
          const dbUserId = await api.getUserDatabaseUUID(user.uid);
          if (dbUserId) {
            setDatabaseUserId(dbUserId);
            console.log('Set database user_id after creating profile:', dbUserId);
          } else {
            console.warn('No database user_id found, falling back to Firebase UID');
            setDatabaseUserId(user.uid);
          }
        } catch (error) {
          console.error('Error fetching database user_id:', error);
          setDatabaseUserId(user.uid);
        }
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setDatabaseUserId(user.uid); // Fallback to Firebase UID
    }
  };

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        const token = await user.getIdToken();
        api.setAuthToken(token);
        
        // Only fetch the profile if we're not in the signup process
        if (!skipProfileFetch.current) {
          try {
            await fetchUserProfile(user);
          } catch (error) {
            console.error('Error fetching user profile might be a new user:', error);
          }
        } else {
          // Reset the flag after we've skipped once
          console.log('[AuthContext] Skipping profile fetch as signup is in progress');
          skipProfileFetch.current = false;
        }
      } else {
        api.setAuthToken(null);
        setUserProfile(null);
        setDatabaseUserId(null);
      }
      
      setIsLoading(false);
    });

    return unsubscribe;
  }, []);

  // Proactive token refresh - refresh token every 45 minutes
  useEffect(() => {
    let tokenRefreshInterval: NodeJS.Timeout;

    if (currentUser) {
      console.log('🔄 Setting up automatic token refresh every 45 minutes');
      
      tokenRefreshInterval = setInterval(async () => {
        try {
          console.log('🔄 Proactively refreshing token...');
          const newToken = await currentUser.getIdToken(true); // Force refresh
          api.setAuthToken(newToken);
          console.log('✅ Token refreshed successfully');
        } catch (error) {
          console.error('❌ Proactive token refresh failed:', error);
        }
      }, 45 * 60 * 1000); // 45 minutes
    }

    return () => {
      if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval);
        console.log('🔄 Cleared token refresh interval');
      }
    };
  }, [currentUser]);

  // Login function
  const login = async (email: string, password: string): Promise<User> => {
    const response = await signInWithEmailAndPassword(auth, email, password);
    const token = await response.user.getIdToken();
    api.setAuthToken(token);
    await fetchUserProfile(response.user);
    return response.user;
  };

  // Login with Google
  const loginWithGoogle = async (profile?: { connection?: string; zip_code?: string; ward?: string }): Promise<User> => {
    const provider = new GoogleAuthProvider();
    const response = await signInWithPopup(auth, provider);
    const user = response.user;
    const token = await user.getIdToken();
    api.setAuthToken(token);
    
    // Check if we already have a database UUID cached for this user
    const cachedUUID = await api.getUserDatabaseUUID(user.uid); 
    if (cachedUUID) {
      console.log("[Google Login] Found cached database UUID:", cachedUUID);
      
      // Since we already have a valid database UUID, 
      // the user must exist in the database. No need to create a new profile.
      // Just set the state with the UID and UUID we already have.
      setUserProfile({
        uid: user.uid,
        displayName: user.displayName || user.email?.split('@')[0] || 'User',
        connection: '',  // We might not know this, but it's ok
        email: user.email || '',
        database_uuid: cachedUUID
      });
      setDatabaseUserId(cachedUUID);
      
      console.log("[Google Login] Using existing database UUID, skipping profile creation");
      return user;
    }
    
    // If we get here, we don't have a cached UUID, so we need to check if the profile exists
    try {
      console.log("[Google Login] No cached UUID, trying to fetch profile by Firebase UID");
      const existingProfile = await api.getUserProfileByUid(user.uid);
      
      if (existingProfile) {
        console.log("[Google Login] Found existing profile:", existingProfile);
        setUserProfile(existingProfile);
        
        // Set database user_id if available in profile
        if (existingProfile.database_uuid) {
          setDatabaseUserId(existingProfile.database_uuid);
          console.log("[Google Login] Using existing database_uuid:", existingProfile.database_uuid);
        } else {
          // Only make this call if we don't have a database_uuid in the profile
          const dbUserId = await api.getUserDatabaseUUID(user.uid);
          setDatabaseUserId(dbUserId || user.uid);
        }
      } else {
        // Create a new profile if one doesn't exist
        console.log("[Google Login] No existing profile found, creating new one");
        const newProfile: UserProfile = {
          uid: user.uid,
          displayName: user.displayName || user.email?.split('@')[0] || 'User',
          connection: profile?.connection || '', // Use provided connection or empty
          email: user.email || '',
          zip_code: profile?.zip_code,
          ward: profile?.ward
        };
        
        // Create the profile and use its database_uuid directly
        const createdProfile = await api.createUserProfile(newProfile);
        console.log("[Google Login] Profile created:", createdProfile);
        
        // Use the created profile data (which should include database_uuid)
        if (createdProfile) {
          setUserProfile(createdProfile);
          
          // Use the database_uuid directly from the created profile
          if (createdProfile.database_uuid) {
            setDatabaseUserId(createdProfile.database_uuid);
            console.log("[Google Login] Using database_uuid from created profile:", createdProfile.database_uuid);
          } else {
            setDatabaseUserId(user.uid);
            console.log("[Google Login] No database_uuid in created profile, using Firebase UID");
          }
        } else {
          // Fallback if profile creation returned null
          setUserProfile(newProfile);
          setDatabaseUserId(user.uid);
        }
      }
    } catch (error) {
      // Handle API errors more specifically
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        // 404 means profile not found - create a new one
        console.log("[Google Login] Profile not found (404), creating new one");
        try {
          const newProfile: UserProfile = {
            uid: user.uid,
            displayName: user.displayName || user.email?.split('@')[0] || 'User',
            connection: profile?.connection || '', // Use provided connection or empty
            email: user.email || '',
            zip_code: profile?.zip_code,
            ward: profile?.ward
          };
          
          const createdProfile = await api.createUserProfile(newProfile);
          
          if (createdProfile) {
            setUserProfile(createdProfile);
            setDatabaseUserId(createdProfile.database_uuid || user.uid);
          } else {
            setUserProfile(newProfile);
            setDatabaseUserId(user.uid);
          }
        } catch (createError) {
          console.error("[Google Login] Error creating profile:", createError);
          // Still allow login but with just Firebase data
          setUserProfile({
            uid: user.uid,
            displayName: user.displayName || user.email?.split('@')[0] || 'User',
            connection: '',
            email: user.email || ''
          });
          setDatabaseUserId(user.uid);
        }
      } else {
        // For other errors, log and continue with just Firebase data
        console.error('[Google Login] Error handling profile:', error);
        setUserProfile({
          uid: user.uid,
          displayName: user.displayName || user.email?.split('@')[0] || 'User',
          connection: '',
          email: user.email || ''
        });
        setDatabaseUserId(user.uid);
      }
    }
    
    return user;
  };

  // Signup function with profile data
  const signup = async (email: string, password: string, profile: { displayName: string; connection: string; zip_code?: string; ward?: string }): Promise<User> => {
    console.log("[AuthContext Signup] Starting signup process for:", email);
    try {
      // Set flag to skip profile fetch when onAuthStateChanged fires
      skipProfileFetch.current = true;
      
      // First, check if a user with this email already exists in Firebase
      // This can be done by attempting to sign in with wrong credentials and checking the error code
      console.log("[AuthContext Signup] Checking if user already exists...");
      try {
        await signInWithEmailAndPassword(auth, email, 'dummy-password-to-check-existence');
        // If we reach here, the user exists but password was wrong
        console.log("[AuthContext Signup] User exists check: Found existing user.");
        throw new Error("An account with this email already exists. Please use the login form instead.");
      } catch (signInError: any) {
        // This specific error means email exists but password was wrong - expected
        if (signInError.code === 'auth/wrong-password') {
          console.log("[AuthContext Signup] User exists check: Found existing user (wrong password).");
          throw new Error("An account with this email already exists. Please use the login form instead.");
        }
        // Any other error (like user-not-found) means we can proceed with signup
        console.log("[AuthContext Signup] User exists check: User does not exist or other error, proceeding.", signInError.code);
      }
      
      // Continue with normal sign up flow...
      console.log("[AuthContext Signup] Attempting Firebase user creation...");
      const response = await createUserWithEmailAndPassword(auth, email, password);
      const user = response.user;
      console.log("[AuthContext Signup] Firebase user created successfully. UID:", user.uid);
      
      // Check if the user already has a profile in the database
      try {
        console.log("[AuthContext Signup] Checking if user already has a database profile...");
        const existingProfile = await api.getUserProfileByUid(user.uid);
        
        if (existingProfile) {
          console.log("[AuthContext Signup] User already has a database profile:", existingProfile);
          // Update local state with the existing profile
          setUserProfile(existingProfile);
          if (existingProfile.database_uuid) {
            setDatabaseUserId(existingProfile.database_uuid);
          } else {
            setDatabaseUserId(user.uid);
          }
          
          // Skip the profile creation steps
          console.log("[AuthContext Signup] Skipping profile creation as profile exists");
          return user;
        }
      } catch (error) {
        // ONLY proceed with creation if we get a 404 (user not found) OR 422 (validation error)
        // 422 means the API couldn't process the request, which in this case likely means
        // the user profile doesn't exist yet
        if (axios.isAxiosError(error) && (error.response?.status === 404 || error.response?.status === 422)) {
          console.warn(`[AuthContext Signup] No profile found (${error.response?.status}), will create a new one`);
        } else {
          console.error("[AuthContext Signup] Error checking for existing profile:", error);
          skipProfileFetch.current = false;
          throw new Error("Unable to verify if you already have an account. Please try again.");
        }
      }
      
      // Update displayName in Firebase Auth
      console.log("[AuthContext Signup] Attempting to update Firebase profile...");
      await updateProfile(user, {
        displayName: profile.displayName
      });
      console.log("[AuthContext Signup] Firebase profile updated successfully.");
      
      // Get token for API call
      console.log("[AuthContext Signup] Attempting to get Firebase ID token...");
      const token = await user.getIdToken();
      console.log("[AuthContext Signup] Firebase ID token retrieved successfully.");
      api.setAuthToken(token);
      console.log("[AuthContext Signup] API auth token set.");
      
      // Store profile in Postgres via API
      console.log("[AuthContext Signup] Attempting to call api.createUserProfile...");
      
      // Create the user profile object
      const userProfileData: UserProfile = {
        uid: user.uid,
        displayName: profile.displayName,
        connection: profile.connection,
        email: email,
        zip_code: profile.zip_code,
        ward: profile.ward
      };
      
      console.log("[AuthContext Signup] User profile data to send:", userProfileData);
      
      // Force API client to make a real API call regardless of environment
      const createdProfile = await api.createUserProfile(userProfileData);
      console.log("[AuthContext Signup] api.createUserProfile call completed. Result:", createdProfile);
      
      // Update local state
      console.log("[AuthContext Signup] Updating local user profile state...");
      setUserProfile({
        uid: user.uid,
        displayName: profile.displayName,
        connection: profile.connection,
        email: email,
        database_uuid: createdProfile?.database_uuid,
        zip_code: profile.zip_code,
        ward: profile.ward
      });
      
      // Set the database_uuid if we got one back
      if (createdProfile?.database_uuid) {
        setDatabaseUserId(createdProfile.database_uuid);
        console.log("[AuthContext Signup] Set database user_id:", createdProfile.database_uuid);
      } else {
        setDatabaseUserId(user.uid);
        console.log("[AuthContext Signup] No database_uuid returned, using Firebase UID");
      }
      
      console.log("[AuthContext Signup] Local state updated.");
      console.log("[AuthContext Signup] Signup process completed successfully.");
      return user;
    } catch (error) {
      console.error('[AuthContext Signup] Error during signup process:', error); // Make sure error is logged clearly
      // Reset the flag since signup failed
      skipProfileFetch.current = false;
      throw error; // Rethrow the error so the calling component knows it failed
    }
  };

  // Update user profile
  const updateUserProfile = async (profile: { displayName: string; connection: string; zip_code?: string; ward?: string }): Promise<void> => {
    if (!currentUser) throw new Error('User not authenticated');
    
    // Update profile in Postgres via API
    await api.updateUserProfile(currentUser.uid, {
      displayName: profile.displayName,
      connection: profile.connection,
      zip_code: profile.zip_code,
      ward: profile.ward
    });
    
    // Update Firebase Auth display name
    await updateProfile(currentUser, {
      displayName: profile.displayName
    });
    
    // Update local state
    setUserProfile(prevProfile => {
      if (!prevProfile) return null;
      return {
        ...prevProfile,
        displayName: profile.displayName,
        connection: profile.connection,
        zip_code: profile.zip_code,
        ward: profile.ward
      };
    });
  };

  // Logout function
  const logout = async () => {
    await signOut(auth);
    api.setAuthToken(null);
    setUserProfile(null);
    setDatabaseUserId(null);
  };

  // Get ID token for API requests
  const getIdToken = async (): Promise<string | null> => {
    if (!currentUser) return null;
    return await currentUser.getIdToken(true);
  };

  const value = {
    currentUser,
    userProfile,
    isAuthenticated: !!currentUser,
    isLoading,
    databaseUserId,
    login,
    loginWithGoogle,
    signup,
    logout,
    getIdToken,
    updateUserProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {!isLoading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 